import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import {
  AnswerCheckboxGroup,
  AnswerRadioGroup,
} from "@/app/components/legacy-components";
import { ROUTES } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import { useBoolean } from "@/shared/hooks";
import { GENDER } from "@/shared/models";
import { FIELD_TYPE } from "@/shared/models/legacy-constants";
import { Alert, Button, Heading, Label } from "@/shared/ui";
import { BestFitModal, UrgentCareModal, WellBeingModal } from "./components";
import { getSymptomQuestions } from "./utils";
import type { SymptomAnswerValue } from "./utils";

export const SymptomsForm = ({ onSubmit }: { onSubmit: () => void }) => {
  const [answers, setAnswers] = useState<{
    answer1?: SymptomAnswerValue;
    answer2?: SymptomAnswerValue[];
  }>();
  const gender = usePortalUserStore((state) => state.portalUser?.gender);
  const navigate = useNavigate();
  const {
    value: showWellBeingModal,
    setTrue: openWellBeingModal,
    setFalse: closeWellBeingModal,
  } = useBoolean(false);
  const {
    value: showUrgentCareModal,
    setTrue: openUrgentCareModal,
    setFalse: closeUrgentCareModal,
  } = useBoolean(false);
  const {
    value: showBestFitModal,
    setTrue: openBestFitModal,
    setFalse: closeBestFitModal,
  } = useBoolean(false);

  const handleNext = () => {
    if (answers?.answer1 === "none" && answers?.answer2?.[0] === "none") {
      onSubmit();
    }
    if (answers?.answer1 === "suicidalThoughts") {
      openWellBeingModal();
    }
    if (answers?.answer1 === "chestPain" || answers?.answer1 === "seizures") {
      openUrgentCareModal();
    }
    if (answers?.answer1 === "none" && answers?.answer2?.[0] !== "none") {
      openBestFitModal();
    }
  };

  const canProceed = !!answers?.answer1 && !!answers?.answer2?.length;
  return (
    <div className="flex flex-col gap-4 pb-8">
      {getSymptomQuestions().map((question) => (
        <div key={question.question} className="flex flex-col gap-4 pb-8">
          {question.type === FIELD_TYPE.RADIO && (
            <>
              <Label htmlFor={question.id} className="text-neutral-900">
                {question.question}
              </Label>

              <AnswerRadioGroup
                id={question.id}
                onValueChange={(value: SymptomAnswerValue) =>
                  setAnswers({ ...answers, answer1: value })
                }
                options={question.answers}
              />
            </>
          )}

          {question.type === FIELD_TYPE.CHECKBOX && (
            <>
              <Label htmlFor={question.id} className="text-neutral-900">
                {question.question}
              </Label>
              <AnswerCheckboxGroup
                id={question.id}
                options={question.answers}
                value={answers?.answer2 ?? []}
                onChange={(value) =>
                  setAnswers({
                    ...answers,
                    answer2: value,
                  })
                }
              />
            </>
          )}
        </div>
      ))}
      {gender === GENDER.FEMALE && (
        <Alert>
          <Heading size="xx-small" level={3} color="primary">
            {t`Note to female patients of reproductive potential`}:
          </Heading>

          <p>
            {t`Please note that we strongly recommend that you use an effective method of contraception during treatment with a GLP-1 medication and for at least 2 months thereafter. For those taking Zepbound or Mounjaro (tirzepatide), we recommend switching to a non-oral contraceptive method or adding a barrier method of contraception for four weeks after initiation and for four weeks after each dose escalation.`}
          </p>
        </Alert>
      )}

      <div className="flex flex-col-reverse items-stretch justify-between gap-4 md:flex-row md:items-center">
        <Button
          onClick={() => navigate(ROUTES.CONSULTATIONS.PURPOSE)}
          variant="secondary"
          size="lg"
        >
          {t`Back`}
        </Button>
        <Button
          onClick={handleNext}
          type="submit"
          size="lg"
          disabled={!canProceed}
        >
          {t`Next`}
        </Button>
      </div>

      <WellBeingModal show={showWellBeingModal} onClose={closeWellBeingModal} />
      <UrgentCareModal
        show={showUrgentCareModal}
        onClose={closeUrgentCareModal}
      />
      <BestFitModal show={showBestFitModal} onClose={closeBestFitModal} />
    </div>
  );
};
